import { keepPreviousData, queryOptions } from "@tanstack/react-query"
import { z } from "zod"
import { PROJECT_STATUSES } from "@/features/project-status/core/projectStatus"
import { reportsQueryKeys } from "@/lib/queryKeys"
import {
  getMaterialsOverviewReport,
  type MaterialsOverviewParams,
} from "../../server/materialsOverviewServer"

export const MaterialsOverviewSearchParams = z.object({
  status: z.enum(PROJECT_STATUSES).optional(),
  countryIds: z.array(z.string()).optional(),
  // Pagination is client-side, but kept in the URL for shareable state. It is
  // intentionally NOT part of the query key, so paging never refetches.
  pageIndex: z.coerce.number().int().min(0).optional(),
  pageSize: z.coerce.number().int().min(1).max(200).optional(),
})

export type MaterialsOverviewSearchValues = z.infer<typeof MaterialsOverviewSearchParams>

export const MATERIALS_OVERVIEW_DEFAULTS = {
  status: "approved" as const,
  pageIndex: 0,
  pageSize: 25,
}

export type MaterialsOverviewViewParams = MaterialsOverviewParams & {
  pageIndex: number
  pageSize: number
}

export const applyMaterialsOverviewDefaults = (
  s: MaterialsOverviewSearchValues,
): MaterialsOverviewViewParams => ({
  status: s.status ?? MATERIALS_OVERVIEW_DEFAULTS.status,
  countryIds: s.countryIds,
  pageIndex: s.pageIndex ?? MATERIALS_OVERVIEW_DEFAULTS.pageIndex,
  pageSize: s.pageSize ?? MATERIALS_OVERVIEW_DEFAULTS.pageSize,
})

/**
 * Query key covers only the server-side filters (status + countries). Page
 * index/size are deliberately excluded so client-side paging reuses the
 * already-computed selection instead of refetching.
 */
export const materialsOverviewQuery = (params: MaterialsOverviewParams) =>
  queryOptions({
    queryKey: reportsQueryKeys.materialsOverview({
      status: params.status,
      countryIds: params.countryIds,
    }),
    queryFn: () =>
      getMaterialsOverviewReport({
        data: { status: params.status, countryIds: params.countryIds },
      }),
    placeholderData: keepPreviousData,
  })
